import type { DeepPartial } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useSearchQuery, useSearchRecentQuery } from '../api'
import { MAX_SEARCH_RESULTS } from '../constants'
import {
  applyNextSearch as _applyNextSearch,
  clearSelection as _clearSelection,
  setSearchType as _setSearchType,
  updateSelection as _updateSelection,
  searchSelectors,
  setNextSearchOptions,
  updateActiveSearchOptions,
  updateNextSearchOptions,
  updateSearchMetaOptions,
} from '../slice'
import type {
  SearchFilterState,
  SearchMetaOptions,
  SearchQueryResult,
  SearchResultItem,
  SearchType,
} from '../types'

export const useSearch = () => {
  const dispatch = useAppDispatch()

  // Top-level selector calls needed for RTK Query skip flags
  const searchType = useAppSelector(searchSelectors.selectSearchType)
  const payload = useAppSelector(searchSelectors.selectSearchPayload)

  // Slice actions
  const updateNext = useCallback(
    (nextSearchParams: DeepPartial<SearchFilterState>) =>
      dispatch(updateNextSearchOptions(nextSearchParams)),
    [dispatch],
  )

  const setNext = useCallback(
    (nextSearchParams: SearchFilterState) =>
      dispatch(setNextSearchOptions(nextSearchParams)),
    [dispatch],
  )

  const updateActive = useCallback(
    (activeSearchParams: DeepPartial<SearchFilterState>) =>
      dispatch(updateActiveSearchOptions(activeSearchParams)),
    [dispatch],
  )

  const updateMetaOptions = useCallback(
    (searchOptions: DeepPartial<SearchMetaOptions>) =>
      dispatch(updateSearchMetaOptions(searchOptions)),
    [dispatch],
  )

  const applyNextSearch = useCallback(
    () => dispatch(_applyNextSearch()),
    [dispatch],
  )

  const clearSelection = useCallback(
    () => dispatch(_clearSelection()),
    [dispatch],
  )

  const updateSelection = useCallback(
    (searchResultItem: SearchResultItem | SearchResultItem[]) => {
      dispatch(_updateSelection(searchResultItem))
    },
    [dispatch],
  )

  const setSearchType = useCallback(
    (searchType: SearchType) => dispatch(_setSearchType(searchType)),
    [dispatch],
  )

  // API fetch — hooks called unconditionally at the top level

  const fallbackData = {
    data: [],
    totalCount: 0,
    documentCount: 0,
  } satisfies SearchQueryResult

  const isSearch = searchType === 'standard' && !!payload
  const isRecent = searchType === 'recent'

  const { data: searchResult, ...searchRequest } = useSearchQuery(payload, {
    skip: !isSearch,
  })

  const { data: recentResult, ...recentRequest } = useSearchRecentQuery(
    { skip: payload?.options.skip ?? 0, take: payload?.options.size ?? 25 },
    { skip: !isRecent },
  )

  let result: SearchQueryResult = fallbackData
  if (isSearch && searchResult) {
    result = searchResult ?? fallbackData
  }
  if (isRecent && recentResult) {
    result = recentResult
  }

  const request = searchRequest ?? recentRequest ?? null
  const { data: rows, ...details } = result

  // Cap total count at MAX_SEARCH_RESULTS
  const cappedTotalCount = Math.min(details.totalCount, MAX_SEARCH_RESULTS)
  const exceedsMax = details.totalCount > MAX_SEARCH_RESULTS

  const searchResults = {
    rows,
    details: {
      ...details,
      totalCount: cappedTotalCount,
      exceedsMax,
    },
    request,
  }

  return {
    searchResults,

    selectors: {
      selectActiveSearchCriteriaString:
        searchSelectors.selectActiveSearchCriteriaString,
      selectSearchPayload: searchSelectors.selectSearchPayload,
      selectActiveSearchOptions: searchSelectors.selectActiveSearchParameters,
      selectNextSearchParameters: searchSelectors.selectNextSearchParameters,
      selectActiveSearchQueryParams:
        searchSelectors.selectActiveSearchQueryParams,
      selectMetaOptions: searchSelectors.selectSearchMetaOptions,
      selectBypassedFieldRequirements:
        searchSelectors.selectBypassedFieldRequirements,
      selectCurrentSelectionRows: searchSelectors.selectCurrentSelectionRows,
      selectCurrentSelectionRowIds: searchSelectors.selectCurrentSelectionRowIds,
      selectBulkEditQueryString: searchSelectors.selectBulkEditQueryString,
      selectSearchType: searchSelectors.selectSearchType,
      selectSearchQueryForExport: searchSelectors.selectSearchQueryForExport,
      selectHasExported: searchSelectors.selectHasExported,
    },

    updateActive,
    updateNext,
    setNext,
    applyNextSearch,
    updateMetaOptions,
    setSearchType,

    updateSelection,
    clearSelection,
  }
}
